import { useState } from 'react'
import type { StaffMember } from '@fitting-room/contracts'
import { STAFF_MARKS, isCalendarMark, normalizeMark } from '@fitting-room/contracts/calendar-marks'
import { useUpdateTeamMember } from '../../lib/adminQueries'

/**
 * A team member's calendar mark: the emoji their appointments carry on the
 * calendar. Ported from tperent's `CalendarMarkPicker`.
 *
 * A native `<select>` rather than a grid of buttons: the shortlist is twenty
 * items, the control sits in a list row, and a native picker brings keyboard
 * support, the phone's system wheel and a decent touch target for free.
 *
 * Marks already taken by someone else are left out rather than shown disabled
 * — a team of three would otherwise scroll past seventeen dead entries — and
 * the member's own mark stays listed so picking it again is not a dead end.
 * The list can be stale (two people picking at once), so the API's uniqueness
 * check is the real arbiter and its 409 gets its own message.
 *
 * The twenty are a shortcut, not the vocabulary: "Custom…" opens a box to
 * paste any single emoji.
 */

/** The "Custom…" option. Not an emoji, so it can never be somebody's mark. */
const CUSTOM = '__custom__'

export function CalendarMarkPicker({
  staffToken,
  member,
  team,
}: {
  readonly staffToken: string | null
  readonly member: StaffMember
  readonly team: readonly StaffMember[]
}) {
  const save = useUpdateTeamMember(staffToken, member.id)
  const [customOpen, setCustomOpen] = useState(false)
  const [draft, setDraft] = useState('')
  // A bad paste, answered here; kept apart from the save's own error so
  // reopening the box after a failed save does not show a stale message.
  const [draftError, setDraftError] = useState(false)

  const takenByOthers = new Set(team.filter((m) => m.id !== member.id && m.calendarMark).map((m) => m.calendarMark))
  const available = STAFF_MARKS.filter((mark) => !takenByOthers.has(mark))
  // A custom mark is not on the shortlist, so without its own option the
  // select would match nothing and draw blank — which reads as "my pick was lost".
  const own = member.calendarMark
  const ownIsCustom = own !== undefined && !(STAFF_MARKS as readonly string[]).includes(own)

  function pick(mark: string | null) {
    save.mutate(
      { calendarMark: mark },
      {
        onSuccess: () => {
          setCustomOpen(false)
          setDraft('')
        },
      },
    )
  }

  function onSelect(value: string) {
    setDraftError(false)
    if (value === CUSTOM) {
      // Opening the box is not a pick; nothing is saved until they submit.
      setCustomOpen(true)
      return
    }
    setCustomOpen(false)
    // The empty option is the select's only way to say "none"; the API is sent null.
    pick(value === '' ? null : value)
  }

  function submitCustom() {
    const mark = normalizeMark(draft)
    if (!isCalendarMark(mark)) {
      setDraftError(true)
      return
    }
    setDraftError(false)
    pick(mark)
  }

  return (
    <div className="flex flex-col gap-1">
      <select
        className="st-select text-base"
        value={customOpen ? CUSTOM : (own ?? '')}
        disabled={save.isPending}
        onChange={(event) => onSelect(event.target.value)}
        aria-label={`${member.displayName}'s calendar mark`}
      >
        <option value="">None</option>
        {ownIsCustom ? <option value={own}>{own}</option> : null}
        {available.map((mark) => (
          <option key={mark} value={mark}>
            {mark}
          </option>
        ))}
        <option value={CUSTOM}>Custom…</option>
      </select>

      {customOpen ? (
        <div className="flex items-center gap-1.5">
          <input
            className="st-input w-24 text-base"
            value={draft}
            // Long enough for a ZWJ sequence, short enough that a paragraph
            // cannot be pasted in; the real check is `isCalendarMark`.
            maxLength={16}
            placeholder="Paste an emoji"
            aria-label={`Custom calendar mark for ${member.displayName}`}
            onChange={(event) => {
              setDraft(event.target.value)
              setDraftError(false)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submitCustom()
              }
            }}
          />
          <button type="button" className="st-btn-quiet" disabled={save.isPending} onClick={submitCustom}>
            Use
          </button>
        </div>
      ) : null}

      {draftError ? (
        <p className="st-alert text-xs" role="alert">
          Paste a single emoji — just one, and no text.
        </p>
      ) : save.isError ? (
        <p className="st-alert text-xs" role="alert">
          {save.error.code === 'calendar_mark_taken'
            ? 'Someone else just took that one. Pick another.'
            : save.error.code === 'invalid_member'
              ? 'Paste a single emoji — just one, and no text.'
              : `That didn't save: ${save.error.message}`}
        </p>
      ) : null}
    </div>
  )
}
